import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { api, fmtCHF, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, FileText } from "lucide-react";
import Sheet from "@/components/invoices/Sheet";
import DateField, { localToday as todayStr } from "@/components/invoices/DateField";
import PaymentChoice, { isPaymentValid, paymentPayload, paymentFor } from "@/components/invoices/PaymentChoice";

export const CATEGORIES = ["Boissons", "Viandes", "Légumes", "Pain", "Poisson", "Produits laitiers", "Énergie", "Loyer", "Personnel", "Autre"];
export const catLabel = (c) => tx(`cat.${c}`, { defaultValue: c });
export const TVA_RATES = [8.1, 3.8, 2.6, 0.0];
const hintsOf = (r) => ({ cashDate: r.cash_date || r.invoice_date, dueDate: r.due_date });

const toRow = (r) => ({
  include: true,
  source: r.pages?.length ? `${r.filename} · p. ${r.pages.join("-")}` : r.filename,
  supplier: r.supplier || "",
  invoice_number: r.invoice_number || "",
  invoice_date: r.invoice_date || "",
  amount: r.amount ? String(r.amount) : "",
  tva_rate: TVA_RATES.includes(r.tva_rate) ? r.tva_rate : 8.1,
  tva_amount: r.tva_amount != null && r.tva_amount > 0 ? String(r.tva_amount) : "",
  due_date: r.due_date || "",
  payment: null,
  cash_date: r.paid_date || r.invoice_date || "",
  category: CATEGORIES.includes(r.category) ? r.category : "Autre",
  dups: [],
});

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}><span className="text-[11px] text-text-secondary block mb-1">{label}</span>{children}</label>
);

export default function BatchReviewSheet({ results, onClose, onSaved }) {
  const [rows, setRows] = useState(() => results.filter((r) => r.success).map(toRow));
  const failed = results.filter((r) => !r.success);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!rows.length) return;
    api.post("/invoices/check-duplicates", { items: rows.map((r) => ({ supplier: r.supplier, invoice_number: r.invoice_number, amount: parseFloat(r.amount) || 0, invoice_date: r.invoice_date, due_date: r.due_date })) })
      .then(({ data }) => setRows((prev) => prev.map((r, i) => ({ ...r, dups: data.matches[i] || [], include: r.include && !(data.matches[i] || []).length }))))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (i, k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const applyToAll = (src) => setRows((prev) => prev.map((r) => {
    if (r.dups.length) return r;
    const p = paymentFor(src.method, hintsOf(r));
    if (src.method === "virement") p.date = src.date;
    if (src.method === "a_payer") p.date = r.due_date || src.date;
    return { ...r, payment: p };
  }));

  const selected = rows.filter((r) => r.include);
  const missing = selected.filter((r) => !isPaymentValid(r.payment)).length;
  const noDate = selected.filter((r) => !r.invoice_date).length;
  const save = async () => {
    if (noDate) { toast.error(tx("tpl.date_required", { count: noDate })); return; }
    if (missing) { toast.error(tx("tpl.choose_payment", { count: missing })); return; }
    setSaving(true);
    let ok = 0, refused = 0;
    for (const r of selected) {
      try {
        await api.post("/invoices", {
          supplier: r.supplier.trim(), amount: parseFloat(String(r.amount).replace(",", ".")) || 0,
          due_date: r.payment.method === "a_payer" ? r.payment.date : (r.due_date || r.invoice_date || todayStr()),
          category: r.category, notes: `Scannée depuis ${r.source}`, recurring: false,
          tva_rate: parseFloat(r.tva_rate) || 0, tva_amount: r.tva_amount === "" ? null : parseFloat(String(r.tva_amount).replace(",", ".")),
          invoice_number: r.invoice_number.trim(), invoice_date: r.invoice_date,
          ...paymentPayload(r.payment),
        });
        ok++;
      } catch (e) { if (e.response?.status === 409) refused++; }
    }
    setSaving(false);
    toast.success(`${tx("plural.saved_invoices", { count: ok })}${refused ? ` · ${tx("plural.refused_dups", { count: refused })}` : ""}${ok + refused < selected.length ? ` · ${tx("plural.failures", { count: selected.length - ok - refused })}` : ""}`);
    onSaved();
  };

  return (
    <Sheet
      wide
      testId="batch-review-sheet"
      title={tx("plural.detected_invoices", { count: rows.length })}
      onClose={onClose}
      footer={
        <button onClick={save} disabled={saving || !selected.length || missing > 0 || noDate > 0} data-testid="batch-review-save" className="btn-primary w-full">
          {saving ? tx("onb.saving") : noDate ? tx("tpl.date_to_enter", { n: noDate }) : missing ? tx("tpl.payment_to_choose", { n: missing }) : tx("tpl.save_n", { count: selected.length })}
        </button>
      }
    >
      <p className="text-xs text-text-secondary">{tx("ui_batchreviewsheet.k6e34f2")}</p>
      {rows.map((r, i) => (
        <div key={i} className={`rounded-2xl border p-3 sm:p-4 space-y-3 ${r.include ? "border-c bg-white" : "border-dashed border-c bg-surface-muted opacity-80"}`} data-testid={`batch-row-${i}`}>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={r.include} disabled={r.dups.length > 0} onChange={upd(i, "include")} className="w-5 h-5 accent-[var(--primary)] shrink-0" data-testid={`batch-row-include-${i}`} />
            <FileText className="w-4 h-4 text-text-secondary shrink-0" />
            <span className="text-[11px] text-text-secondary truncate flex-1">{r.source}</span>
            <span className="font-display text-base whitespace-nowrap">{fmtCHF(parseFloat(r.amount) || 0)}</span>
          </div>
          {r.dups.length > 0 && (
            <div className="flex items-start gap-2 p-2 rounded-xl bg-[#FFF8EB] border border-[#F5C87A]" data-testid={`batch-row-dup-${i}`}>
              <AlertTriangle className="w-4 h-4 text-[#B45309] shrink-0 mt-0.5" />
              <p className="text-xs text-[#92400E]">{tx("tpl.possible_dup")} {r.dups[0].supplier} {r.dups[0].invoice_number ? `${tx("inv.no")} ${r.dups[0].invoice_number}` : ""} · {fmtCHF(r.dups[0].amount)} · {fmtDate(r.dups[0].date)} {r.dups[0].in_batch ? tx("tpl.dup_in_batch") : tx("tpl.dup_saved")} {tx("tpl.dup_skip")}</p>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Field label={tx("ui_batchreviewsheet.k73d163")} className="col-span-2"><input value={r.supplier} onChange={upd(i, "supplier")} className="input-c text-sm" data-testid={`batch-row-supplier-${i}`} /></Field>
            <Field label={tx("ui_batchreviewsheet.kd72b78")}><input value={r.invoice_number} onChange={upd(i, "invoice_number")} className="input-c text-sm" /></Field>
            <Field label={tx("ui_batchreviewsheet.kfdac99")}><DateField value={r.invoice_date} onChange={(d) => setRow(i, { invoice_date: d })} testId={`batch-row-date-${i}`} /></Field>
            <Field label={tx("ui_batchreviewsheet.k4d0b7d")}><input inputMode="decimal" value={r.amount} onChange={upd(i, "amount")} className="input-c text-sm" data-testid={`batch-row-amount-${i}`} /></Field>
            <Field label={tx("ui_batchreviewsheet.kcb23d8")}>
              <select value={r.tva_rate} onChange={upd(i, "tva_rate")} className="input-c text-sm">{TVA_RATES.map((x) => <option key={x} value={x}>{x} %</option>)}</select>
            </Field>
            <Field label={tx("ui_batchreviewsheet.ke0c4f5")} className="col-span-2"><input inputMode="decimal" value={r.tva_amount} onChange={upd(i, "tva_amount")} className="input-c text-sm" /></Field>
            <Field label={tx("ui_batchreviewsheet.k8c6b7f")} className="col-span-2">
              <select value={r.category} onChange={upd(i, "category")} className="input-c text-sm">{CATEGORIES.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}</select>
            </Field>
          </div>
          {r.include && (
            <div className="p-3 bg-surface-muted rounded-xl space-y-2" data-testid={`batch-row-payment-${i}`}>
              <p className="text-sm font-semibold">{tx("ui_batchreviewsheet.k1b91ea")}</p>
              <PaymentChoice value={r.payment} onChange={(p) => setRow(i, { payment: p })} hints={hintsOf(r)} testPrefix={`batch-row-${i}-payment`} />
              {r.payment && rows.length > 1 && (
                <button type="button" onClick={() => applyToAll(r.payment)} className="w-full h-11 rounded-xl border border-primary-c text-primary-c font-semibold text-sm" data-testid={`batch-row-apply-all-${i}`}>
                  {tx("ui_batchreviewsheet.k7641b9")}
                </button>
              )}
            </div>
          )}
        </div>
      ))}
      {failed.length > 0 && (
        <div className="rounded-2xl bg-red-50 p-3 text-xs text-red-700" data-testid="batch-review-failed">
          {failed.map((f, i) => <p key={i}>{f.filename}{f.page ? ` p. ${f.page}` : ""} : {f.error || "illisible"}</p>)}
        </div>
      )}
    </Sheet>
  );
}
